<template>
  <div v-if="showInfoCard">
    <b-button
      id="show-deletion-modal-btn"
      variant="transparent"
      class="deletion-info-card btn btn-link text-left d-flex justify-content-between w-100 align-items-center font-weight-normal mb-5 px-3 py-2 rounded bg-primary text-white"
      @click="showDeletionModal"
    >
      <div>
        <span class="font-weight-bold">{{ $t('settings.delete_user.deletion_process.info_card.in_process') }}</span>

        <span class="d-block small">{{ $t('settings.delete_user.deletion_process.info_card.view_status') }}</span>
      </div>

      <sdui-icon class="icon-chevron-right" />
    </b-button>

    <SModal
      v-model="showDialog"
      :header-title="$t('settings.delete_user.deletion_process.info_card.title')"
      centered
      size="lg"
      body-class="text-center"
      return-focus="#show-deletion-modal-btn"
      no-stacking
      :show-footer-gradient="false"
    >
      <template v-if="showDialog">
        <p class="mb-1 text-muted">{{ $t('settings.delete_user.deletion_process.info_card.current_status') }}</p>
        <h3 v-if="deleteRequestApproved">{{ $t('settings.delete_user.deletion_process.info_card.approved') }}</h3>
        <h3 v-else>{{ $t('settings.delete_user.deletion_process.info_card.in_review') }}</h3>
        <sdui-stepper :items="listItems" />
      </template>
      <template #modal-footer>
        <div class="w-100">
          <div v-if="deleteRequestApproved">
            <p v-if="deletionIsCancelable"
               id="cancel-description"
               class="text-muted"
               v-html="$sanitize(cancelTimeLeft)"
            />
            <p v-else id="cancel-description">{{ $t('settings.delete_user.deletion_process.info_card.cancel_not_allowed') }}</p>
          </div>
          <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
          <b-button
            variant="primary"
            size="lg"
            class="w-100"
            :disabled="!deletionIsCancelable"
            aria-describedby="cancel-description"
            @click="
              showDialog = false;
              showDialog2 = true;
            "
          >
            {{ $t('settings.delete_user.deletion_process.info_card.cancel_deletion') }}
          </b-button>
        </div>
      </template>
    </SModal>

    <SConfirmModal
      v-model="showDialog2"
      :title="$t('settings.delete_user.deletion_process.info_card.title')"
      :description="$t('settings.delete_user.deletion_process.info_card.cancel_confirm')"
      :ok-title="$t('settings.delete_user.deletion_process.info_card.cancel_deletion')"
      :cancel-title="$t('settings.delete_user.deletion_process.info_card.cancel_call_off')"
      no-stacking
      @ok="cancelDeletionRequest"
      @hide="showDialog2 = false"
    />

    <SModal
      v-model="showDialog3"
      :header-title="$t('settings.delete_user.deletion_process.info_card.title')"
      centered
      return-focus="#show-deletion-modal-btn"
      :show-footer-gradient="false"
    >
      <div v-if="showDialog3" class="text-center mt-5">
        <sdui-icon class="icon icon-check success deletion-request__icon" />
        <h2 class="fz-base mt-2 mb-0">{{ $t('settings.delete_user.deletion_process.info_card.cancel_deletion_success') }}</h2>
      </div>
      <template #modal-footer>
        <b-button variant="primary"
                  size="lg"
                  block
                  class="w-100"
                  @click="closeInfoCardPermanently"
        >
          {{ $t('global.close') }}
        </b-button>
      </template>
    </SModal>
  </div>
</template>

<script>
import Users from 'src/api/users.js'
import { User } from 'src/store/models/index.js'
import SduiStepper from 'src/components/miscellaneous/ui/SduiStepper.vue'

export default {
  name: 'UserDeletionInfo',

  components: {
    SduiStepper,
  },

  data: () => ({
    showInfoCard: false,
    deleteRequestApproved: false,
    deletionReversionWindow: null,
    showDialog: false,
    showDialog2: false,
    showDialog3: false,
  }),

  computed: {
    userId() {
      // Banner should display always relative to the current user;
      return this.$me.id
    },

    /**
     * Calculates the time left until the user can't cancel the deletion anymore (only cancel for 48 hours after request)
     * @returns {string} The time left until the user deletion isn't cancel anymore, in hours or minutes.
     */
    cancelTimeLeft() {
      const deadlineDate = this.$moment(this.deletionReversionWindow)
      const nowDate = this.$moment()
      const hoursLeft = deadlineDate.diff(nowDate, 'hours')
      const minutesLeft = deadlineDate.diff(nowDate, 'minutes')

      return this.$t('settings.delete_user.deletion_process.info_card.cancel_countdown', {
        countdown:
          hoursLeft >= 1
            ? `${hoursLeft} ${this.$t('settings.delete_user.deletion_process.info_card.hours', hoursLeft)}`
            : minutesLeft > 0
              ? `${minutesLeft} ${this.$t('settings.delete_user.deletion_process.info_card.minute', minutesLeft)}`
              : this.$t('settings.delete_user.deletion_process.info_card.less_than_minute'),
      })
    },

    deletionIsCancelable() {
      // before admin approval, user can cancel the deletion request anytime
      if (!this.deleteRequestApproved) return true

      const deadlineDate = this.$moment(this.deletionReversionWindow)

      return this.$moment().isBefore(deadlineDate)
    },

    listItems() {
      const iconOnGoing = 'icon-radio-checked primary'
      const iconSuccess = 'icon-check success'

      const STATE_DEFAULT = [
        {
          text: this.$t('settings.delete_user.deletion_process.info_card.in_review'),
          icon: iconOnGoing,
        },
        {
          text: this.$t('settings.delete_user.deletion_process.info_card.cancel_info'),
        },
      ]
      const STATE_APPROVED = [
        {
          text: this.$t('settings.delete_user.deletion_process.info_card.in_review'),
          icon: iconSuccess,
        },
        {
          text: this.$t('settings.delete_user.deletion_process.info_card.cancel_info'),
          icon: this.deletionIsCancelable ? iconOnGoing : iconSuccess,
        },
      ]
      const currentState = this.deleteRequestApproved ? STATE_APPROVED : STATE_DEFAULT

      return [
        {
          text: this.$t('settings.delete_user.deletion_process.info_card.requestion_sent'),
          icon: iconSuccess,
        },
        ...currentState,
      ]
    },
  },

  methods: {
    showDeletionModal() {
      this.showDialog = !this.showDialog
      this.getUserDeletionStatus()
    },

    async cancelDeletionRequest() {
      try {
        this.showDialog2 = false
        this.showDialog3 = true
        await User.cancelAccountDeletion(this.userId)
      } catch (e) {
        console.error('Request could not be cancelled', e)
      }
    },

    async getUserDeletionStatus() {
      if (!this?.$me?.school?.properties?.['users.deletion_request.enabled']) return

      try {
        const { data } = await Users.getUserDeletionRequestStatus(this.userId)

        this.deletionReversionWindow = data.deletion_reversion_window
        this.deleteRequestApproved = data.deletion_request_approved
        this.showInfoCard = data.deletion_request_submitted
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        this.showInfoCard = false
      }
    },

    closeInfoCardPermanently() {
      this.showDialog3 = false
      this.showInfoCard = false
    },
  },

  async beforeMount() {
    await this.getUserDeletionStatus()
  },
}
</script>

<style scoped lang="scss">
.deletion-info-card {
  box-shadow: $sdui-shadow;
}

.deletion-info-card--border {
  border: 2px solid $color-primary;

  &:hover {
    background-color: $color-gray-100;
  }
}

.deletion-request__icon {
  font-size: pxtorem(70);
}
</style>
